import { Box, Stack, useTheme, IconButton, Button } from "@mui/material";
import { Footer } from "../../components/Footer.tsx";
import { FoodsNavbar } from "@foodpilot/foods";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";
import { Navigate, useLocation, useNavigate } from "react-router";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "../../components/Selector/LanguageSelector/LanguageSelector.tsx";
import { CompanySelectorSidebar } from "src/components/Sidebar/CompanySelectorSidebar.tsx";
import { ProfilingToggle } from "src/components/Sidebar/ProfilingToggle.tsx";
import { BuildInfoPopover } from "src/components/Sidebar/BuildInfoPopover.tsx";
import { useMenuBasedNavigation } from "./useMenuBasedNavigation.tsx";
import { UserService } from "src/services/UserService.ts";
import { URLHandler } from "src/services/URLHandler.ts";
import { LocalStorage } from "src/services/LocalStorage.ts";
import MenuIcon from "@mui/icons-material/Menu";
import ManageAccountsOutlined from "@mui/icons-material/ManageAccountsOutlined";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { CONSENT_GOVERNANCE_FEATURE_SWITCH } from "src/api/client-api/consent/constants";
import { useRTLLayout } from "src/utils/useRTLLayout";
import { isLocalEnv } from "src/utils/getEnv";

const OPENED_SIDEBAR = "open" as const;
const navbarNoUnderlineSx = {
    "& .MuiButton-root:hover": {
        textDecoration: "none",
    },
};

type IBaseTemplateProps = {
    sidebarStatus: string | null;
    children: ReactNode | ReactNode[];
};

export const BaseTemplate = (props: IBaseTemplateProps) => {
    const { sidebarStatus, children } = props;

    const theme = useTheme();
    const {
        user,
        userCompanies,
        selectedCompany,
        pendingConsentItems = [],
        pendingConsentPreferences = [],
    } = useFoodpilotContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const isPdfMode = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get("__pdf") === "1";
    }, [location.search]);

    // Utilisation de nos hooks centralisés
    const { isMobile } = useResponsiveBreakpoints();
    const { isRTL, getContentMargin, getFlexDirection } = useRTLLayout();

    const { applicationParameters, isFeatureActivated } = useApplicationParameters();
    const isConsentGovernanceEnabled = isFeatureActivated(CONSENT_GOVERNANCE_FEATURE_SWITCH);
    const [logoSquareError, setLogoSquareError] = useState(false);

    const getSidebarImage = useCallback(() => {
        if (applicationParameters?.logoSquare && !logoSquareError) {
            return applicationParameters.logoSquare;
        }
        return null;
    }, [applicationParameters?.logoSquare, logoSquareError]);

    useEffect(() => {
        if (applicationParameters?.logoSquare && !logoSquareError) {
            const img = new Image();
            img.onload = () => {};
            img.onerror = () => {
                setLogoSquareError(true);
            };
            img.src = applicationParameters.logoSquare;
        }
    }, [applicationParameters?.logoSquare, logoSquareError]);

    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [sidebarExpanded, setSidebarExpanded] = useState(sidebarStatus === OPENED_SIDEBAR);
    const [userMenuInstance, setUserMenuInstance] = useState(0);

    // Calculate current sidebar width based on breakpoints
    const getCurrentSidebarWidth = () => {
        if (sidebarExpanded) {
            return "280px";
        }

        if (isMobile) {
            return "0px";
        }

        return "80px";
    };

    const currentSidebarWidth = isPdfMode ? "0px" : getCurrentSidebarWidth();
    const contentMargin = isPdfMode ? {} : getContentMargin(currentSidebarWidth, isMobile);

    const handleLayout = useCallback(
        (isOpen: boolean) => {
            if (isMobile) {
                // En mobile, on ignore complètement les appels de FoodsNavbar
                // Le drawer mobile est géré uniquement par le hamburger menu
                return;
            } else {
                LocalStorage.setItem("sidebarStatus", isOpen ? "open" : "closed");
                setSidebarExpanded(isOpen);
            }
        },
        [isMobile],
    );

    const buttons = useMenuBasedNavigation();

    const closeUserMenu = useCallback(() => {
        // FoodsNavbar owns the user popover state, so remounting it closes the menu.
        setUserMenuInstance((currentInstance) => currentInstance + 1);
    }, []);

    const openProfileSettings = useCallback(() => {
        closeUserMenu();
        setMobileDrawerOpen(false);
        navigate(URLHandler.profileSettings.root());
    }, [closeUserMenu, navigate]);

    const buildUserActions = useCallback(() => {
        const actions = [
            <Button
                key="profileSettings"
                variant="text"
                startIcon={<ManageAccountsOutlined />}
                sx={{ justifyContent: "flex-start", textTransform: "none", width: "100%" }}
                onClick={openProfileSettings}
            >
                {t("sidebar.user_actions.tooltip_label", "Manage my profile")}
            </Button>,
            <LanguageSelector key="languageSelector" />,
            <BuildInfoPopover key="buildInfo" />,
        ];

        if (isLocalEnv()) {
            actions.push(<ProfilingToggle key="profilingToggle" />);
        }

        return actions;
    }, [openProfileSettings, t]);

    const userActions = useMemo(() => buildUserActions(), [buildUserActions]);

    const connectedUserConfig = useMemo(
        () => ({
            ...user.trustedUser,
            tooltipLabel: t("sidebar.user_actions.tooltip_label", "Manage my profile"),
        }),
        [user.trustedUser, t],
    );

    // Mémoriser les objets pour éviter les re-renders
    const organizationConfig = useMemo(
        () => ({
            name: selectedCompany?.name ?? "FoodPilot",
            picture: getSidebarImage() ?? undefined,
            tooltipLabel: t("sidebar.organization.tooltip_label", "Change organization"),
            popover:
                (userCompanies?.length ?? 0) > 1 ?
                    {
                        element: <CompanySelectorSidebar userCompanies={userCompanies ?? []} />,
                    }
                :   undefined,
        }),
        [selectedCompany?.name, getSidebarImage, userCompanies, t],
    );

    const sidebarTextsConfig = useMemo(
        () => ({
            shortenSidebar: t("reduire_la_barre_de_navigation"),
            logout: UserService.isImpersonationActive() ? t("auth.finish_impersonation") : t("se_deconnecter"),
        }),
        [t],
    );

    const logoutAction = useCallback(() => {
        const isImpersonating = UserService.isImpersonationActive();
        if (isImpersonating) {
            UserService.unimpersonateUser();
            return;
        }
        navigate(URLHandler.login.logout());
    }, [navigate]);

    // Mobile hamburger menu mémorisé
    const mobileHamburgerMenu = useMemo(
        () =>
            isMobile && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 7,
                        [isRTL ? "right" : "left"]: 7,
                        zIndex: 1200,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <IconButton
                        onClick={() => {
                            setMobileDrawerOpen(true);
                        }}
                        sx={{
                            padding: "12px",
                            color: "white",
                            backgroundColor: theme.palette.primary.main,
                            "&:hover": { backgroundColor: theme.palette.primary.dark },
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>
            ),
        [isMobile, isRTL, theme],
    );

    // Navigation component mémorisé
    const navigationComponent = useMemo(
        () => (
            <FoodsNavbar
                key={`desktop-${userMenuInstance}`}
                buttons={buttons}
                connectedUser={connectedUserConfig}
                organization={organizationConfig}
                sidebarTexts={sidebarTextsConfig}
                sidebarStatusHandler={handleLayout}
                sidebarOptions={{
                    isExpanded: sidebarExpanded,
                    notExpandedSize: "80px",
                    expandedSize: "280px",
                }}
                logoutAction={logoutAction}
                userActions={userActions}
            />
        ),
        [
            buttons,
            connectedUserConfig,
            organizationConfig,
            sidebarTextsConfig,
            handleLayout,
            sidebarExpanded,
            logoutAction,
            userActions,
            userMenuInstance,
        ],
    );

    const hasPendingConsents = pendingConsentItems.length > 0 || pendingConsentPreferences.length > 0;

    if (isConsentGovernanceEnabled && hasPendingConsents && !UserService.isImpersonationActive()) {
        return <Navigate to={URLHandler.login.consents()} replace />;
    }

    return (
        <Stack
            direction={getFlexDirection("row")}
            spacing={0}
            useFlexGap
            justifyContent="center"
            sx={{
                minHeight: "100%",
            }}
        >
            {!isPdfMode && mobileHamburgerMenu}

            {/* Mobile Navigation Drawer */}
            {!isPdfMode && isMobile && mobileDrawerOpen && (
                <>
                    {/* Backdrop transparent invisible pour détecter les clics en dehors */}
                    <Box
                        sx={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100vw",
                            height: "100vh",
                            backgroundColor: "transparent",
                            zIndex: 1299, // Juste en dessous du drawer
                        }}
                        onClick={() => {
                            setMobileDrawerOpen(false);
                        }}
                    />

                    {/* Drawer personnalisé sans overlay */}
                    <Box
                        sx={{
                            position: "fixed",
                            top: 0,
                            [isRTL ? "right" : "left"]: 0,
                            width: 280,
                            height: "100vh",
                            backgroundColor: "#ffffff",
                            zIndex: 1300,
                            boxShadow: isRTL ? "-2px 0 8px rgba(10, 10, 10, 0.1)" : "2px 0 8px rgba(0,0,0,0.1)",
                            overflow: "hidden",
                            transform: `translateX(${
                                mobileDrawerOpen ? "0"
                                : isRTL ? "100%"
                                : "-100%"
                            })`,
                            transition: "transform 0.3s ease-in-out",
                        }}
                    >
                        <Box
                            sx={{
                                height: "100%",
                                width: "100%",
                                overflow: "hidden",
                                ...navbarNoUnderlineSx,
                            }}
                        >
                            <FoodsNavbar
                                key={`mobile-${userMenuInstance}`}
                                buttons={buttons}
                                connectedUser={connectedUserConfig}
                                organization={organizationConfig}
                                sidebarTexts={{
                                    shortenSidebar: t("reduire_la_barre_de_navigation"),
                                    logout:
                                        UserService.isImpersonationActive() ?
                                            t("auth.finish_impersonation")
                                        :   t("se_deconnecter"),
                                }}
                                sidebarStatusHandler={() => {}} // Pas de handler en mode mobile drawer
                                sidebarOptions={{
                                    isExpanded: true, // FORCÉ À TRUE
                                    notExpandedSize: "280px", // Même taille que le drawer
                                    expandedSize: "280px", // Même taille que le drawer
                                }}
                                navigationMode="mobile"
                                onMobileDrawerClose={() => setMobileDrawerOpen(false)}
                                logoutAction={() => {
                                    const isImpersonating = UserService.isImpersonationActive();
                                    if (isImpersonating) {
                                        UserService.unimpersonateUser();
                                        return;
                                    }
                                    navigate(URLHandler.login.logout());
                                }}
                                userActions={userActions}
                            />
                        </Box>
                    </Box>
                </>
            )}

            {/* Desktop/Tablet Navigation */}
            {!isMobile && !isPdfMode && (
                <Box
                    sx={{
                        position: "fixed" as const,
                        top: 0,
                        [isRTL ? "right" : "left"]: "0",
                        zIndex: 1000,
                        ...navbarNoUnderlineSx,
                    }}
                >
                    <ExtensionPoint extensionPoint={EXTENSION_POINTS.DASHBOARD_SIDEBAR_TOP} />
                    {navigationComponent}
                    <ExtensionPoint extensionPoint={EXTENSION_POINTS.DASHBOARD_SIDEBAR_BOTTOM} />
                </Box>
            )}

            {/* Main Content */}
            <Box
                sx={{
                    "--app-navigation-overlap": sidebarExpanded ? theme.spacing(2.5) : theme.spacing(0),
                    width: "100%",
                    maxWidth: isPdfMode || isMobile ? "100%" : `calc(100% - ${currentSidebarWidth})`,
                    minHeight: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    backgroundColor: isPdfMode ? theme.palette.common.white : theme.custom.grey[300],
                    ...contentMargin,
                    transition: "margin .3s ease, max-width .3s ease",
                    padding: 0,
                }}
            >
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.GLOBAL_HEADER} />
                <Box>{children}</Box>
                {!isPdfMode && (
                    <Box>
                        <Footer organizationId={selectedCompany?.id} />
                        <ExtensionPoint extensionPoint={EXTENSION_POINTS.GLOBAL_FOOTER} />
                    </Box>
                )}
            </Box>
        </Stack>
    );
};
